<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
  allowLoggedInOnly: true
</route>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useMessagingStore } from '@/stores/messaging.store'
import MessagingInbox from '@core/components/MessagingInbox.vue'
import MessagingThread from '@core/components/MessagingThread.vue'
import StartConversationDialog from '@core/components/StartConversationDialog.vue'
import { useUsersStore } from '@/stores'
import { storeToRefs } from 'pinia'

const route = useRoute()
const router = useRouter()
const messagingStore = useMessagingStore()
const { myUser } = storeToRefs(useUsersStore())
const showNewConvDialog = ref(false)
const showThread = ref(false)

// Feature flag: restaurant messaging is not yet available
const RESTAURANT_MESSAGING_ENABLED = false

const preselectedRestaurantId = ref(null)
const preselectedReservationId = ref(null)

const activeConversationId = computed(() => messagingStore.activeConversationId)

onMounted(async () => {
  await messagingStore.fetchConversations()

  // Support ?conv=... query param
  const convId = route.query.conv
  if (convId) {
    selectConversation(convId)
  }

  // Support opening from Booking "Message Restaurant"
  if (RESTAURANT_MESSAGING_ENABLED && route.query.restaurantId) {
    preselectedRestaurantId.value = route.query.restaurantId
    preselectedReservationId.value = route.query.reservationId || null
    showNewConvDialog.value = true
  }
})

function selectConversation(convId) {
  messagingStore.activeConversationId = convId
  showThread.value = true
  router.replace({ query: { ...route.query, conv: convId } })
}

function handleConversationCreated(convId) {
  showNewConvDialog.value = false
  preselectedRestaurantId.value = null
  preselectedReservationId.value = null
  selectConversation(convId)
  messagingStore.fetchConversations()
}

function handleBack() {
  if (showThread.value) {
    handleCloseThread()
  } else {
    router.push(`/m/${myUser.value.profileId}`)
  }
}

function handleCloseThread() {
  showThread.value = false
  messagingStore.clearActiveConversation()
}
</script>

<template>
  <div class="messages-page d-flex flex-column h-100">
    <a
      v-if="myUser?.profileId"
      class="back-to-profile"
      @click="handleBack"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      ><path d="m15 18-6-6 6-6" /></svg>
      Back
    </a>
    <!-- Desktop: two-panel layout -->
    <div class="messages-layout d-flex w-100 flex-1">
      <!-- Inbox panel -->
      <div
        class="inbox-panel"
        :class="{ 'd-none d-md-flex': showThread }"
      >
        <MessagingInbox
          :active-conversation-id="activeConversationId"
          @select="selectConversation"
          @new="showNewConvDialog = true"
        />
      </div>

      <!-- Thread panel -->
      <div
        class="thread-panel flex-1"
        :class="{ 'd-none d-md-flex': !showThread }"
      >
        <div
          v-if="!activeConversationId"
          class="d-flex flex-column align-center justify-center h-100 text-medium-emphasis"
        >
          <VIcon
            size="48"
            class="mb-3"
          >
            mdi-message-outline
          </VIcon>
          <span>Select a conversation to start messaging</span>
          <VBtn
            color="primary"
            variant="tonal"
            class="mt-4"
            @click="showNewConvDialog = true"
          >
            New Message
          </VBtn>
        </div>

        <MessagingThread
          v-else
          :conversation-id="activeConversationId"
          @close="handleCloseThread"
        />
      </div>
    </div>

    <!-- New conversation dialog -->
    <StartConversationDialog
      v-model="showNewConvDialog"
      :preselected-restaurant-id="preselectedRestaurantId"
      :preselected-reservation-id="preselectedReservationId"
      @conversation-created="handleConversationCreated"
    />
  </div>
</template>

<style scoped>
.back-to-profile {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px 4px;
  font-size: 14px;
  font-weight: 500;
  color: var(--ds-brand-primary-dark, #3F998F);
  text-decoration: none;
  cursor: pointer;
  flex-shrink: 0;
}

.back-to-profile:hover {
  text-decoration: underline;
}

.messages-page {
  height: calc(100vh - 64px);
  overflow: hidden;
}

.messages-layout {
  height: 100%;
}

.inbox-panel {
  width: 320px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.thread-panel {
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  min-width: 0;
}

@media (max-width: 960px) {
  .inbox-panel {
    width: 100%;
  }
}
</style>
